import {useUpdatedRef} from '@sail/react';
import {noop, once} from '@sail/utils';
import {useMemo} from 'react';
import {UninitializedObservabilityProviderError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import {useObservabilityApi} from 'src/internal/provider/ObservabilityApiProvider';

import type {IAnalytics} from 'src/internal/analytics/types';
import type {IObservabilityApi} from 'src/internal/types';

const logUninitializedErrorOnce = once(() => {
  logErrorInDev(new UninitializedObservabilityProviderError('useAnalytics'));
});

// WeakMap to store analytics objects by provider instance
const providerAnalyticsMap = new WeakMap<IObservabilityApi, IAnalytics>();

const defaultAnalytics: IAnalytics = {
  action: noop,
  link: noop,
  modal: noop,
  track: noop,
  viewed: noop,
};

/**
 * @see https://sail.stripe.me/apis/observability/useAnalytics
 */
export default function useAnalytics(): IAnalytics {
  const api = useObservabilityApi();
  const apiRef = useUpdatedRef(api);

  return useMemo((): IAnalytics => {
    // eslint-disable-next-line react-hooks/refs
    const currentApiValue = apiRef.current;

    // eslint-disable-next-line react-hooks/refs
    if (!currentApiValue) {
      logUninitializedErrorOnce();

      return defaultAnalytics;
    }

    // Check if we already have analytics for this provider
    let analytics = providerAnalyticsMap.get(currentApiValue);

    if (!analytics) {
      analytics = {
        action(...args: Parameters<IAnalytics['action']>): void {
          return apiRef.current?.analytics?.action(...args);
        },

        link(...args: Parameters<IAnalytics['link']>): void {
          return apiRef.current?.analytics?.link(...args);
        },

        modal(...args: Parameters<IAnalytics['modal']>): void {
          return apiRef.current?.analytics?.modal(...args);
        },

        track(...args: Parameters<IAnalytics['track']>): void {
          return apiRef.current?.analytics?.track(...args);
        },

        viewed(...args: Parameters<IAnalytics['viewed']>): void {
          return apiRef.current?.analytics?.viewed(...args);
        },
      } as IAnalytics;

      // eslint-disable-next-line react-hooks/refs
      providerAnalyticsMap.set(currentApiValue, analytics);
    }

    return analytics;
  }, [
    apiRef, // This is a ref object, so it never changes and thus the returned object is stable.
  ]);
}
